import type { Theme } from '@mui/material';
import { Button, Card, CardHeader, Divider, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import { UpdateDestinationKioskDialog } from '../../organisms/dialogs/update-destination-kiosk-dialog/update-destination-kiosk-dialog';

import { useOrganizationKiosks } from '@/features/shared/hooks/query/use-organization-kiosks';
import { useDialog } from '@/hooks/use-dialog';
import { getLabel } from '@/utils/get-label';

interface DestinationKioskProps {
  id: number;
  allowedKioskIds: number[];
  deviceIds: number[];
  hosts: { id: number; firstName: string; lastName: string; email: string }[];
  hostObligatory: boolean;
  hostConfirmationObligatory: boolean;
  addHostStepEnabled: boolean;
  hostConfirmationInEmail: boolean;
  deviceConfirmation: boolean;
  defaultHostId: number | null;
  kioskVisitHours: number;
}

export const DestinationKiosk: FC<DestinationKioskProps> = props => {
  const {
    id,
    allowedKioskIds,
    deviceIds,
    hosts,
    hostObligatory,
    hostConfirmationObligatory,
    addHostStepEnabled,
    hostConfirmationInEmail,
    deviceConfirmation,
    defaultHostId,
    kioskVisitHours,
    ...other
  } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';
  const { t } = useTranslation(['destination', 'common']);
  const { data: organizationKiosks } = useOrganizationKiosks();

  const isKioskEnabled = (allowedKioskIds?.length || 0) > 0;
  const hasAssignedDevices = (deviceIds?.length || 0) > 0;
  const isDefaultHostAssigned = !!defaultHostId;

  const effectiveAddHostStepEnabled = isDefaultHostAssigned ? false : addHostStepEnabled;
  const effectiveHostConfirmationObligatory = hostConfirmationObligatory;
  const effectiveHostConfirmationInEmail = hostConfirmationInEmail;
  const effectiveDeviceConfirmation = deviceConfirmation && isKioskEnabled && hasAssignedDevices;

  const defaultHost = hosts.find(host => host.id === defaultHostId);
  const defaultHostName = defaultHost
    ? getLabel({
        values: [defaultHost.firstName, defaultHost.lastName],
        fallback: defaultHost.email || '-',
      })
    : t('destination:kiosk.defaultHostNotSet');

  const selectedKioskNames = (organizationKiosks || [])
    .filter(({ id }) => allowedKioskIds.includes(id))
    .map(({ name }) => name);

  const [
    isOpenUpdateDestinationKioskDialog,
    handleOpenDestinationKioskModalOpen,
    handleCloseUpdateDestinationKioskDialog,
  ] = useDialog();

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:kiosk.cardTitle')}
          action={
            <Button
              onClick={handleOpenDestinationKioskModalOpen}
              color="primary"
              variant="text"
              size="large">
              {t('common:button.edit')}
            </Button>
          }
        />
        <Divider />
        <PropertyList>
          <PropertyListItem
            align={align}
            label={t('destination:kiosk.allowedKiosks')}
            value={
              selectedKioskNames.length > 0
                ? selectedKioskNames.join(', ')
                : t('destination:kiosk.noKiosksSelected')
            }
          />
          <PropertyListItem
            align={align}
            label={t('destination:kiosk.defaultHost')}
            value={defaultHostName}>
            <Typography color="textSecondary" variant="body2" sx={{ wordBreak: 'break-word' }}>
              {defaultHostName}
            </Typography>
          </PropertyListItem>

          {isKioskEnabled && (
            <PropertyListItem
              align={align}
              label={t('destination:kiosk.addHostStepEnabled')}
              value={effectiveAddHostStepEnabled ? t('common:yes') : t('common:no')}
            />
          )}
          {isKioskEnabled && effectiveAddHostStepEnabled && (
            <PropertyListItem
              align={align}
              label={t('destination:kiosk.hostObligatory')}
              value={hostObligatory ? t('common:yes') : t('common:no')}
            />
          )}
          {isKioskEnabled && (effectiveAddHostStepEnabled || isDefaultHostAssigned) && (
            <PropertyListItem
              align={align}
              label={t('destination:kiosk.hostConfirmationObligatory')}
              value={effectiveHostConfirmationObligatory ? t('common:yes') : t('common:no')}
            />
          )}
          {isKioskEnabled && (effectiveAddHostStepEnabled || isDefaultHostAssigned) && (
            <PropertyListItem
              align={align}
              label={t('destination:kiosk.hostConfirmationInEmail')}
              value={effectiveHostConfirmationInEmail ? t('common:yes') : t('common:no')}
            />
          )}
          {isKioskEnabled && (
            <PropertyListItem
              align={align}
              label={t('destination:kiosk.deviceConfirmation')}
              value={effectiveDeviceConfirmation ? t('common:yes') : t('common:no')}
            />
          )}
          {isKioskEnabled && (
            <PropertyListItem
              align={align}
              label={t('destination:kiosk.kioskVisitHours')}
              value={`${kioskVisitHours}h`}
            />
          )}
        </PropertyList>
      </Card>

      <UpdateDestinationKioskDialog
        id={id}
        allowedKioskIds={allowedKioskIds}
        deviceIds={deviceIds}
        hosts={hosts}
        addHostStepEnabled={effectiveAddHostStepEnabled}
        hostObligatory={hostObligatory}
        hostConfirmationObligatory={hostConfirmationObligatory}
        hostConfirmationInEmail={hostConfirmationInEmail}
        deviceConfirmation={effectiveDeviceConfirmation}
        defaultHostId={defaultHostId}
        kioskVisitHours={kioskVisitHours}
        open={isOpenUpdateDestinationKioskDialog}
        onCancel={handleCloseUpdateDestinationKioskDialog}
      />
    </>
  );
};
